import React, { useContext, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { UploadImageContext } from '../../context/UploadImage';
import { useUrlQuery } from '../../hooks/use-url-query';
import { Button, Icon, Loading } from '../../components/UI';
import ImageDropzone from './ImageDropzone';
import { UploadImageEnum } from './UploadImage.enum';
import {
  LIST_DEFAULT_AVATAR,
  SET_PROFILE_AVATAR,
  SET_DEFAULT_PROFILE_AVATAR,
  SET_CURRENT_PROFILE,
} from '../../http/apollo-queries';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { Modal } from '../../components/Modal';
import { logger } from '../../helpers/logger';

const DEFAULT_AVATAR_SRC = 'upload-image.png';

const UploadImage = () => {
  const { t } = useTranslation();
  const { user, refetch } = useAuth();
  const navigate = useNavigate();
  const query = useUrlQuery();
  const {
    uploadImageStep,
    setUploadImageStep,
    croppedImage,
    profileId,
    setProfileId,
    setCroppedImage,
  } = useContext(UploadImageContext);

  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  const [defaultAvatar, setDefaultAvatar] = useState<string>('');
  const [avatarHovered, setAvatarHovered] = useState<boolean>(false);

  const [setCurrentProfile, { loading: loadingSetCurrentProfile }] =
    useMutation(SET_CURRENT_PROFILE, {
      onCompleted: (data) => {
        refetch().then(() => {
          navigate('/account');
        });
      },
      onError: (err) => {
        logger.error(err);
        toast.error(t('chooseAccount.error'));
      },
    });

  const updateAction = !!query.get('profileId');

  const {
    loading: loadingAvatars,
    data: {
      listDefaultAvatar = {
        items: [
          {
            size: {
              original: '',
            },
          },
        ],
      },
    } = {},
  } = useQuery(LIST_DEFAULT_AVATAR);

  const [setProfileAvatar, { loading }] = useMutation(
    updateAction ? SET_PROFILE_AVATAR : SET_DEFAULT_PROFILE_AVATAR
  );

  useEffect(() => {
    if (croppedImage?.id) {
      setSelectedAvatar(croppedImage.id);
    }
  }, [croppedImage]);

  useEffect(() => {
    setDefaultAvatar(listDefaultAvatar.items[0]?.id);
    if (!croppedImage) {
      setSelectedAvatar(listDefaultAvatar.items[0]?.id);
    }
  }, [listDefaultAvatar]);

  useEffect(() => {
    // get profile from params in url else take profile from current user
    if (updateAction) {
      const currentProfile = user?.profiles.items.find(
        (item) => item.id === query.get('profileId')
      );

      if (currentProfile?.id) {
        setProfileId(query.get('profileId'));

        setCroppedImage({
          url: currentProfile?.avatar?.size?.avatarMedium,
          id: currentProfile?.avatar?.id,
        });

        setSelectedAvatar(currentProfile?.avatar?.id);
      }
    } else {
      if (user?.profile?.id) {
        setProfileId(user.profile.id);
      }
    }
  }, [user]);

  const setAvatar = (skip = false) => {
    const variables = {
      profileId,
      mediaId: skip ? defaultAvatar : selectedAvatar,
    };

    setProfileAvatar({ variables })
      .then(() => {
        refetch().then(() => {
          if (updateAction) {
            setUploadImageStep(UploadImageEnum.CHOSE_SECONDARY_AVATAR);
          } else {
            navigate('/choose-plan');
          }
        });
      })
      .catch((err) => {
        logger.error(err);
        toast.error(t('uploadImage.errorOnSaving'));
      });
  };

  return (
    <Modal
      isOpen={true}
      height="auto"
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      padding={'none'}
    >
      <div
        className={'flex flex-col w-full h-full justify-between text-center'}
      >
        {uploadImageStep === UploadImageEnum.CHOOSE_PROFILE_PICTURE && (
          <>
            <div className={'flex mt-4 flex-col flex-grow-1 justify-center'}>
              <h1 className={'text-black text-xl font-semibold mb-4 md:mb-8'}>
                {t('uploadImage.title')}
              </h1>
              <div className={'text-center'}>
                <p className="text-black text-base font-semibold">
                  {t('uploadImage.subtitle')}
                </p>
                <p className="text-grey-30 text-xs">
                  {t('uploadImage.requisites')}
                </p>
              </div>

              <div className={'relative w-32 mx-auto mt-4 md:mt-6 text-center'}>
                {/*AVATAR UPLOADED*/}
                {croppedImage?.id ? (
                  <div>
                    <img
                      src={croppedImage?.url}
                      alt={croppedImage?.filename}
                      className={`cursor-pointer w-32 rounded-full ${
                        croppedImage?.id && selectedAvatar === croppedImage?.id
                          ? 'border'
                          : ''
                      }`}
                      onClick={() => setSelectedAvatar(croppedImage?.id)}
                    />
                    {selectedAvatar === croppedImage?.id && (
                      <Icon
                        className="ml-auto w-6 h-6 rounded-full bg-black p-1 text-lime absolute material-icons text-base top-2 right-2"
                        color="black"
                        svgName={'check'}
                      />
                    )}
                    <Button
                      type="button"
                      variant="transparent"
                      buttonClass="mx-auto"
                      onClick={() =>
                        setUploadImageStep(UploadImageEnum.DROPZONE)
                      }
                    >
                      {t('uploadImage.edit')}
                    </Button>
                  </div>
                ) : (
                  <div
                    className={'overflow-hidden w-32'}
                    onMouseEnter={() => {
                      setAvatarHovered(true);
                    }}
                    onMouseLeave={() => {
                      setAvatarHovered(false);
                    }}
                  >
                    <img
                      src={DEFAULT_AVATAR_SRC}
                      alt="default_avatar"
                      className={`cursor-pointer w-32 rounded-full pointer-events-none ${
                        croppedImage?.id && selectedAvatar === croppedImage?.id
                          ? 'border'
                          : ''
                      }`}
                    />
                    <div
                      className={
                        'bg-black bg-opacity-50 rounded-full flex items-center cursor-pointer justify-center top-0 left-0 absolute w-32 h-32 ' +
                        (avatarHovered ? '' : 'hidden')
                      }
                      onClick={() =>
                        setUploadImageStep(UploadImageEnum.DROPZONE)
                      }
                    >
                      <Icon
                        className="h-12 text-white pointer-events-none"
                        color="black"
                        svgName={'circlePencil'}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="transparent"
                      buttonClass="mx-auto"
                      onClick={() =>
                        setUploadImageStep(UploadImageEnum.DROPZONE)
                      }
                    >
                      {t('uploadImage.addPicture')}
                    </Button>
                  </div>
                )}
              </div>

              <p className="text-black text-base text-center mt-1 md:mt-6 font-semibold dark:text-white">
                {t('uploadImage.orChooseAvatar')}
              </p>
              {loadingAvatars ? (
                <Loading status={loadingAvatars} className={'mx-auto'} />
              ) : (
                <div className="mt-4 flex justify-between w-4/5 mx-auto ">
                  {listDefaultAvatar.items.map((avatar, i) => {
                    const src = avatar.size.original;
                    const selected =
                      (!selectedAvatar && i === 0) ||
                      avatar.id === selectedAvatar;

                    return (
                      <div key={avatar.id} className={'relative'}>
                        {selected && (
                          <Icon
                            key={'icon-' + avatar.id}
                            className="ml-auto w-5 h-5 rounded-full bg-black text-lime p-1 absolute text-base top-0 right-0"
                            color="black"
                            svgName={'check'}
                          />
                        )}
                        <div
                          style={{ backgroundImage: `url("${src}"` }}
                          key={'img-' + avatar.id}
                          className={`w-14 h-14 lg:w-20 lg:h-20 bg-grey-10 bg-contain cursor-pointer rounded-full
                      ${selected && 'border'} `}
                          onClick={() => setSelectedAvatar(avatar.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="mt-2 md:mt-6 flex justify-between w-full items-center p-5">
              <Button
                type="button"
                variant="transparent"
                onClick={() => (updateAction ? navigate(-1) : setAvatar(true))}
              >
                {t(updateAction ? 'uploadImage.cancel' : 'uploadImage.skip')}
              </Button>
              <Button
                type="button"
                variant="primary"
                loading={loading}
                onClick={() => setAvatar()}
              >
                {t('uploadImage.continue')}
              </Button>
            </div>
          </>
        )}
        {uploadImageStep !== UploadImageEnum.CHOOSE_PROFILE_PICTURE &&
          uploadImageStep !== UploadImageEnum.CHOSE_SECONDARY_AVATAR && (
            <>
              <div className={'flex flex-col flex-grow-1 justify-between'}>
                <h1
                  className={
                    'flex items-center justify-center text-black text-xl font-semibold flex-grow-1 py-4'
                  }
                >
                  {t('uploadImage.dropzone.uploadPicture')}
                </h1>
                <ImageDropzone />
              </div>
            </>
          )}
        {uploadImageStep === UploadImageEnum.CHOSE_SECONDARY_AVATAR && (
          <div className="min-w-sm bg-white pt-14 pb-6 flex flex-col justify-center items-center rounded-xl">
            <Icon svgName="circleCheck" />
            <h1 className="mt-8 text-black text-xl font-semibold max-w-xs text-center">
              {t('uploadImage.profileCreated.title')}
            </h1>
            <p className="mt-3 text-grey-50 text-sm max-w-xs text-center">
              {t('uploadImage.profileCreated.subtitle')}
            </p>
            <Button
              type="button"
              variant="primary"
              buttonClass="w-24 mt-8 text-sm"
              onClick={() => {
                setCurrentProfile({
                  variables: {
                    profileId: query.get('profileId'),
                  },
                });
              }}
              loading={loadingSetCurrentProfile}
            >
              {t('uploadImage.profileCreated.done')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UploadImage;
